import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  Download,
  Settings,
  Moon,
  Sun,
  Menu,
  X,
  Mic,
  Calendar,
  DollarSign,
  Receipt,
  BarChart3,
} from "lucide-react";
import PatientList from "./components/PatientList";
import PatientForm from "./components/PatientForm";
import PatientDetail from "./components/PatientDetail";
import Dashboard from "./components/Dashboard";
import VoiceCommands from "./components/VoiceCommands";
import AppointmentList from "./components/AppointmentList";
import AppointmentForm from "./components/AppointmentForm";
import AppointmentCalendar from "./components/AppointmentCalendar";
import TodaysSchedule from "./components/TodaysSchedule";
import FloatingAgent from "./components/FloatingAgent";
import FinancialDashboard from "./components/FinancialDashboard";
import TransactionList from "./components/TransactionList";
import BillingSettings from "./components/BillingSettings";
import FinancialReports from "./components/FinancialReports";
import medicalDB from "./services/database";

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Load theme preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const handleExportData = async () => {
    try {
      const data = await medicalDB.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `medical-records-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${isDarkMode ? "dark" : ""}`}>
      {/* Sticky Top Toolbar */}
      <header className="sticky-toolbar flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 md:hidden text-gray-700"
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <h1 className="text-xl font-bold text-medical-blue">Mardakom</h1>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate("/patients/new")}
            className="btn-primary flex items-center space-x-2"
            data-agent-action="add-patient"
          >
            <UserPlus size={20} />
            <span className="hidden sm:inline">Add Patient</span>
          </button>

          <button
            onClick={handleExportData}
            className="btn-secondary flex items-center space-x-2"
            data-agent-action="export-data"
          >
            <Download size={20} />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside
          className={`sidebar w-64 min-h-screen p-6 transition-transform duration-300 ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 fixed md:relative z-40`}
        >
          <nav className="space-y-4">
            <Link
              to="/patients"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              data-agent-action="view-patients"
            >
              <Users size={20} />
              <span>Patients</span>
            </Link>

            <Link
              to="/appointments"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              data-agent-action="view-appointments"
            >
              <Calendar size={20} />
              <span>Appointments</span>
            </Link>

            <Link
              to="/dashboard"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              data-agent-action="view-dashboard"
            >
              <Settings size={20} />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/voice-commands"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              data-agent-action="view-voice-commands"
            >
              <Mic size={20} />
              <span>Voice Commands</span>
            </Link>

            {/* Financial Section */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Financial
              </p>

              <Link
                to="/financial"
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                data-agent-action="view-financial-dashboard"
              >
                <BarChart3 size={20} />
                <span>Dashboard</span>
              </Link>

              <Link
                to="/financial/transactions"
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                data-agent-action="view-transactions"
              >
                <Receipt size={20} />
                <span>Transactions</span>
              </Link>

              <Link
                to="/financial/settings"
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                data-agent-action="view-billing-settings"
              >
                <DollarSign size={20} />
                <span>Billing Settings</span>
              </Link>

              <Link
                to="/financial/reports"
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                data-agent-action="view-financial-reports"
              >
                <BarChart3 size={20} />
                <span>Reports</span>
              </Link>
            </div>
          </nav>

          {/* Today's Schedule Widget */}
          <TodaysSchedule />
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route path="/" element={<PatientList />} />
            <Route path="/patients" element={<PatientList />} />
            <Route path="/patients/new" element={<PatientForm />} />
            <Route path="/patients/:id" element={<PatientDetail />} />
            <Route path="/patients/:id/edit" element={<PatientForm />} />
            <Route path="/appointments" element={<AppointmentList />} />
            <Route
              path="/appointments/calendar"
              element={<AppointmentCalendar />}
            />
            <Route path="/appointments/new" element={<AppointmentForm />} />
            <Route
              path="/appointments/:id/edit"
              element={<AppointmentForm />}
            />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/voice-commands" element={<VoiceCommands />} />
            <Route path="/financial" element={<FinancialDashboard />} />
            <Route
              path="/financial/dashboard"
              element={<FinancialDashboard />}
            />
            <Route
              path="/financial/transactions"
              element={<TransactionList />}
            />
            <Route path="/financial/settings" element={<BillingSettings />} />
            <Route path="/financial/reports" element={<FinancialReports />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Floating AI Agent */}
      <FloatingAgent />
    </div>
  );
}

export default App;
